import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { imgSrc, inr } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setOpen, setQty, removeItem, subtotal, shipping, count } = useStore();
  const toFree = Math.max(0, 1499 - subtotal);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="cart-overlay"
            className="fixed inset-0 z-[80] bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            key="cart-panel"
            className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col border-l border-line bg-parchment"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
            aria-label="Shopping cart"
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <p className="font-display text-2xl">
                Your cart <span className="font-mono text-sm text-stone2">({count})</span>
              </p>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-2 transition-colors hover:bg-cream"
                data-testid="cart-close-btn"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                <ShoppingBag size={36} strokeWidth={1} className="text-stone-400" />
                <p className="font-display text-2xl">Your cart is empty</p>
                <p className="text-sm text-stone2">Go make it yours — every piece is one of a kind.</p>
                <Link
                  to="/shop"
                  onClick={() => setOpen(false)}
                  className="btn-solid mt-2"
                  data-testid="cart-empty-shop-btn"
                >
                  Browse the shelf
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="cart-items-list">
                  {items.map((i) => (
                    <div key={i.product_id} className="flex gap-4 border-b border-line py-4">
                      <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="shrink-0">
                        <img
                          src={imgSrc(i.image)}
                          alt={i.name}
                          className="h-20 w-20 border border-line object-cover"
                        />
                      </Link>
                      <div className="flex flex-1 flex-col">
                        <div className="flex justify-between gap-2">
                          <p className="font-display text-lg leading-tight">{i.name}</p>
                          <button
                            onClick={() => removeItem(i.product_id)}
                            className="text-stone-400 transition-colors hover:text-terracotta"
                            data-testid={`cart-remove-${i.slug}`}
                            aria-label={`Remove ${i.name}`}
                          >
                            <Trash2 size={16} strokeWidth={1.5} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center border border-stone-300">
                            <button
                              onClick={() => setQty(i.product_id, i.qty - 1)}
                              className="px-2.5 py-1.5 transition-colors hover:bg-cream"
                              data-testid={`cart-minus-${i.slug}`}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={13} />
                            </button>
                            <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${i.slug}`}>
                              {i.qty}
                            </span>
                            <button
                              onClick={() => setQty(i.product_id, i.qty + 1)}
                              className="px-2.5 py-1.5 transition-colors hover:bg-cream"
                              data-testid={`cart-plus-${i.slug}`}
                              aria-label="Increase quantity"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-line px-6 py-5">
                  {toFree > 0 && (
                    <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.15em] text-stone2" data-testid="cart-free-ship-hint">
                      Add {inr(toFree)} more for free shipping
                    </p>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-stone2">Subtotal</span>
                    <span data-testid="cart-subtotal">{inr(subtotal)}</span>
                  </div>
                  <div className="mt-1 flex justify-between text-sm">
                    <span className="text-stone2">Shipping</span>
                    <span>{shipping === 0 ? "Free" : inr(shipping)}</span>
                  </div>
                  <div className="mt-3 flex justify-between border-t border-line pt-3 font-display text-2xl">
                    <span>Total</span>
                    <span data-testid="cart-total">{inr(subtotal + shipping)}</span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={() => setOpen(false)}
                    className="btn-solid mt-5 w-full"
                    data-testid="cart-checkout-btn"
                  >
                    Checkout
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
